/**
 * The catalog's Events tab (Event Tickets plan 2026-10-19, U4, R9): the
 * gym's published ticketed events, soonest first. Read from the public
 * events route; hidden while ENABLE_EVENT_TICKETS is off.
 */

'use client';

import React, { useEffect, useState } from 'react';
import type { PublicTicketedEvent } from '@/utils/eventTickets/publicTicketTypes';
import { publicEventPath } from '@/utils/eventTickets/publicEventPath';
import { storeBrandVars } from '@/utils/store/storeBranding';
import PublicEventCard from './PublicEventCard';
import { PUBLIC_EVENT_COPY } from './publicEventCopy';

/** The catalog tab that shows events; also the ?tab= value the event page links back to. */
export const EVENTS_CATALOG_TAB = 'events';

export function usePublicTicketedEvents(gymSlug: string, enabled: boolean): PublicTicketedEvent[] {
  const [events, setEvents] = useState<PublicTicketedEvent[]>([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetch(`/api/public/events/${encodeURIComponent(gymSlug)}`, { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled && body?.success && Array.isArray(body.events)) setEvents(body.events);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [gymSlug, enabled]);
  return events;
}

export default function PublicEventsList({
  gymSlug,
  events,
  primaryColor,
}: {
  gymSlug: string;
  events: PublicTicketedEvent[];
  primaryColor: string | null;
}) {
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" style={storeBrandVars({ primary_color: primaryColor, secondary_color: null }) as React.CSSProperties} data-testid="catalog-events">
      <h2 className="mb-6 text-2xl font-bold text-gray-900">{PUBLIC_EVENT_COPY.EVENTS_TAB}</h2>
      {events.length === 0 ? (
        <p className="py-16 text-center text-gray-600">{PUBLIC_EVENT_COPY.NO_EVENTS}</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {events.map((e) => (
            <PublicEventCard key={e.id} event={e} href={publicEventPath(gymSlug, e.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
